// src/pages/mlb/MlbCheatSheet.jsx
import React, { useEffect, useState } from "react";
/* ------------------------------- config ------------------------------- */
const CHEAT_URL =
  import.meta.env?.VITE_MLB_CHEAT_SHEET_URL ||
  "/data/mlb/latest/cheat_sheet.json";
const META_URL =
  import.meta.env?.VITE_MLB_META_URL ||
  "/data/mlb/latest/meta.json";
/* ------------------------------ helpers ------------------------------- */
const teamLogo = (abv) => `/logos/mlb/${String(abv || "").toUpperCase()}.png`;
const num = (v) => {
  const n = Number(String(v ?? "").replace(/[,$%\s]/g, ""));
  return Number.isFinite(n) ? n : null;
};
const fmt0 = (v) => {
  const n = num(v);
  return n == null
    ? ""
    : n.toLocaleString(undefined, { maximumFractionDigits: 0 });
};
const fmt1 = (v) => {
  const n = num(v);
  if (n == null) return "";
  return n.toFixed(1).replace(/\.0$/, "");
};
const fmtPct1 = (v) => {
  if (v == null || v === "") return "";
  let s = String(v).trim();
  if (s.endsWith("%")) {
    const n = num(s.slice(0, -1));
    return n == null ? "" : `${n.toFixed(1)}%`;
  }
  let n = num(s);
  if (n == null) return "";
  if (Math.abs(n) <= 1) n *= 100;
  return `${n.toFixed(1)}%`;
};
const time12 = (v) => {
  if (!v) return "";
  const s = String(v).trim();
  if (/\d{1,2}:\d{2}(:\d{2})?\s?[AP]M/i.test(s)) {
    return s.toUpperCase().replace(/\s+/g, " ");
  }
  const m = s.match(/^(\d{1,2}):(\d{2})$/);
  if (!m) return s;
  let hh = Number(m[1]);
  const mm = m[2];
  let ampm = "AM";
  if (hh === 0) hh = 12;
  else if (hh === 12) ampm = "PM";
  else if (hh > 12) {
    hh -= 12;
    ampm = "PM";
  }
  return `${hh}:${mm} ${ampm}`;
};
/* -------------------------------- page -------------------------------- */
export default function MlbCheatSheet() {
  const [cheat, setCheat] = useState({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [updatedStamp, setUpdatedStamp] = useState("");
  const [theme, setTheme] = useState("dark");
  const isDark = theme === "dark";
useEffect(() => {
  const bust = `v=${Date.now()}`;
  (async () => {
    try {
      setError("");
      setLoading(true);
      setUpdatedStamp("");
      const [cheatRes, metaRes] = await Promise.all([
        fetch(`${CHEAT_URL}?${bust}`, { cache: "no-store" }),
        fetch(`${META_URL}?${bust}`, { cache: "no-store" }),
      ]);
      if (!cheatRes.ok) throw new Error(`Cheat sheet HTTP ${cheatRes.status}`);
      const cheatData = await cheatRes.json();
      setCheat(cheatData || {});
      let metaData = {};
      if (metaRes.ok) {
        metaData = await metaRes.json();
      }
      const rawUpdated =
        cheatData?.updated ||
        cheatData?.last_updated ||
        cheatData?.updated_iso ||
        cheatData?.meta?.updated ||
        cheatData?.meta?.last_updated ||
        metaData?.last_updated ||
        metaData?.updated ||
        metaData?.updated_iso;
      if (rawUpdated) {
        const d = new Date(rawUpdated);
        if (!Number.isNaN(d.getTime())) {
          setUpdatedStamp(
            d.toLocaleString(undefined, {
              month: "numeric",
              day: "numeric",
              hour: "numeric",
              minute: "2-digit",
            })
          );
        }
      }
    } catch (e) {
      setError(String(e.message || e));
      setCheat({});
      setUpdatedStamp("");
    } finally {
      setLoading(false);
    }
  })();
}, []);
  /* -------------------------- derived helpers -------------------------- */
  const getSec = (k) => (Array.isArray(cheat?.[k]) ? cheat[k] : []);
  const getSortedBySalarySec = (k) =>
    getSec(k)
      .slice()
      .sort((a, b) => {
        const aSal = num(a?.Salary ?? a?.["DK Sal"]);
        const bSal = num(b?.Salary ?? b?.["DK Sal"]);
        return (bSal ?? -Infinity) - (aSal ?? -Infinity);
      });
  const outerClass = isDark
    ? "min-h-screen bg-slate-950 text-slate-100"
    : "min-h-screen bg-slate-100 text-slate-900";
  const rowOdd = isDark ? "bg-slate-950" : "bg-white";
  const rowEven = isDark ? "bg-slate-900/85" : "bg-gray-50";
  const headBg = isDark
    ? "bg-slate-900/95 text-slate-100 border-slate-800"
    : "bg-slate-100 text-slate-700 border-slate-200";
  const thBase =
    "px-1 py-2 text-center font-semibold text-[10px] uppercase leading-tight [overflow-wrap:anywhere] border-b";
  const tdBase =
    "px-1 py-2 text-center text-[11px] [overflow-wrap:anywhere] align-middle leading-tight";
  const tableClass = `w-full table-fixed border-separate text-sm ${
    isDark ? "text-slate-100" : "text-slate-900"
  }`;
  /* ----------------------------- small bits ---------------------------- */
  function TeamCell({ team }) {
    const abv = String(team || "").toUpperCase();
    const src = teamLogo(abv);
    return (
      <div className="flex items-center justify-center gap-1 min-w-0">
        {abv ? (
          <img
            src={src}
            alt={abv}
            className="w-3.5 h-3.5 object-contain shrink-0"
            onError={(e) => (e.currentTarget.style.display = "none")}
          />
        ) : null}
        <span className="font-medium text-[11px]">{abv}</span>
      </div>
    );
  }
  function PlayerRow({ row, index }) {
    const bg = index % 2 === 0 ? rowOdd : rowEven;
    const opp =
      row.Opp ?? row["Opp "] ?? row.Matchup ?? row["Matchup "] ?? "";
    const vegas =
      row.Vegas ??
      row["Imp. Total"] ??
      row["Imp Total"] ??
      row["Imp. Tot"] ??
      "";
    const proj = row.Proj ?? row["Proj "] ?? row["DK Proj"] ?? "";
    const val = row.Value ?? row["Value "] ?? row["DK Val"] ?? "";
    const pown = row.pOWN ?? row["DK pOWN%"] ?? row["pOWN%"] ?? "";
    return (
      <tr className={bg}>
        <td className={`${tdBase} text-left w-[26%]`}>
          <div className="flex flex-wrap items-center gap-x-1.5 gap-y-0.5 min-w-0">
            <TeamCell team={row.Team} />
            <span className="min-w-0 [overflow-wrap:anywhere]">{row.Player}</span>
          </div>
        </td>
        <td className={`${tdBase} tabular-nums`}>{fmt0(row.Salary)}</td>
        <td className={`${tdBase}`}>{String(row.Team || "").toUpperCase()}</td>
        <td className={`${tdBase}`}>{String(opp).replace(/\s+/g, " ").trim()}</td>
        <td className={`${tdBase} tabular-nums`}>{fmt1(vegas)}</td>
        <td className={`${tdBase}`}>{time12(row.Time)}</td>
        <td className={`${tdBase} tabular-nums`}>{fmt1(proj)}</td>
        <td className={`${tdBase} tabular-nums`}>{fmt1(val)}</td>
        <td className={`${tdBase}`}>{fmtPct1(pown)}</td>
      </tr>
    );
  }
  function StackRow({ row, index }) {
    const bg = index % 2 === 0 ? rowOdd : rowEven;
    const vegas =
      row.Vegas ??
      row["Imp. Tot"] ??
      row["Imp. Total"] ??
      row["Imp Total"] ??
      "";
    const proj = row.Proj ?? row["Proj "] ?? row["DK Proj"] ?? "";
    const val = row.Value ?? row["Value "] ?? row["DK Val"] ?? "";
    const pown = row.pOWN ?? row["DK pOWN%"] ?? row["pOWN%"] ?? "";
    const opp = row.Opp ?? row["Opp "] ?? "";
    return (
      <tr className={bg}>
        <td className={`${tdBase} w-[10%]`}>
          <TeamCell team={row.Team} />
        </td>
        <td className={`${tdBase} tabular-nums w-[12%]`}>
          {fmt0(row.Salary ?? row["DK Sal"])}
        </td>
        <td className={`${tdBase} w-[9%]`}>{String(opp).toUpperCase().trim()}</td>
        <td className={`${tdBase} text-left w-[21%]`}>
          {String(row["Opp Pitcher"] ?? "").trim()}
        </td>
        <td className={`${tdBase} tabular-nums w-[9%]`}>{fmt1(vegas)}</td>
        <td className={`${tdBase} w-[12%]`}>{time12(row.Time)}</td>
        <td className={`${tdBase} tabular-nums w-[9%]`}>{fmt1(proj)}</td>
        <td className={`${tdBase} tabular-nums w-[9%]`}>{fmt1(val)}</td>
        <td className={`${tdBase} w-[9%]`}>{fmtPct1(pown)}</td>
      </tr>
    );
  }
  function Block({ title, children }) {
    const cardBase =
      "rounded-2xl border shadow-[0_12px_30px_rgba(15,23,42,0.18)] overflow-hidden w-full min-w-0";
    const cardClass = isDark
      ? `${cardBase} border-slate-800 bg-slate-950`
      : `${cardBase} border-slate-200 bg-white`;
    const headerClass =
      "bg-sky-700 text-white border-b border-sky-800 px-4 py-2 text-sm font-semibold";
    return (
      <div className={cardClass}>
        <div className={headerClass}>{title}</div>
        {children}
      </div>
    );
  }
  /* ------------------------------ columns ------------------------------ */
  const colsPlayer = [
    "Player",
    "Salary",
    "Team",
    "Opp",
    "Vegas",
    "Time",
    "Proj",
    "Value",
    "pOWN",
  ];
  const colsStacks = [
    "Team",
    "Salary",
    "Opp",
    "Opp Pitcher",
    "Vegas",
    "Time",
    "Proj",
    "Value",
    "pOWN",
  ];
  /* -------------------------------- UI -------------------------------- */
  return (
    <div className={outerClass}>
      <div className="mx-auto max-w-[1800px] px-3 sm:px-5 lg:px-6 py-5 md:py-6">
        <div className="mb-4 flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
          <div className="flex min-w-0 flex-wrap items-baseline gap-x-3 gap-y-1">
            <h1 className="text-2xl font-extrabold tracking-tight md:text-3xl">
              MLB — Cheat Sheet
            </h1>
            <span className={"text-xs italic " + (isDark ? "text-slate-400" : "text-slate-500")}>
              Updated: {updatedStamp || "—"}
            </span>
          </div>
          <div className="flex items-center gap-2 self-start sm:self-auto">
            <span className="text-[11px] text-slate-400">Theme</span>
            <div className="inline-flex rounded-full bg-slate-900/70 p-1 border border-slate-700/70">
              {["dark", "light"].map((t) => {
                const active = theme === t;
                return (
                  <button
                    key={t}
                    onClick={() => setTheme(t)}
                    className={
                      "px-2.5 py-1 rounded-full text-xs transition " +
                      (active
                        ? "bg-sky-500 text-slate-950 shadow"
                        : "text-slate-300 hover:text-white")
                    }
                  >
                    {t === "dark" ? "Dark" : "Light"}
                  </button>
                );
              })}
            </div>
          </div>
        </div>
        {error && (
          <div className="text-red-400 font-semibold mb-3">
            Failed to load: {error}
          </div>
        )}
        {loading ? (
          <div
            className={
              "text-sm " + (isDark ? "text-slate-400" : "text-slate-600")
            }
          >
            Loading…
          </div>
        ) : (
          <div className="grid grid-cols-1 xl:grid-cols-2 items-start gap-4 lg:gap-5">
            <div className="flex min-w-0 flex-col gap-4">
              {["Pitcher", "C", "1B", "2B", "3B", "SS"].map((sec) => (
                <Block key={sec} title={sec}>
                  <div className="w-full min-w-0">
                    <table className={tableClass} style={{ borderSpacing: 0 }}>
                      <thead className={`${headBg} sticky top-0 z-10`}>
                        <tr>
                          <th className={`${thBase} w-[26%]`}>Player</th>
                          <th className={`${thBase}`}>Salary</th>
                          <th className={`${thBase}`}>Team</th>
                          <th className={`${thBase}`}>Opp</th>
                          <th className={`${thBase}`}>Vegas</th>
                          <th className={`${thBase}`}>Time</th>
                          <th className={`${thBase}`}>Proj</th>
                          <th className={`${thBase}`}>Value</th>
                          <th className={`${thBase}`}>pOWN</th>
                        </tr>
                      </thead>
                      <tbody>
                        {getSortedBySalarySec(sec).length === 0 ? (
                          <tr>
                            <td
                              className="px-3 py-3 text-center text-slate-500"
                              colSpan={colsPlayer.length}
                            >
                              No rows.
                            </td>
                          </tr>
                        ) : (
                          getSortedBySalarySec(sec).map((r, i) => (
                            <PlayerRow key={`${sec}-${i}`} row={r} index={i} />
                          ))
                        )}
                      </tbody>
                    </table>
                  </div>
                </Block>
              ))}
            </div>
            <div className="flex min-w-0 flex-col gap-4">
              <Block title="OF">
                <div className="w-full min-w-0">
                  <table className={tableClass} style={{ borderSpacing: 0 }}>
                    <thead className={`${headBg} sticky top-0 z-10`}>
                      <tr>
                        <th className={`${thBase} w-[26%]`}>Player</th>
                        <th className={`${thBase}`}>Salary</th>
                        <th className={`${thBase}`}>Team</th>
                        <th className={`${thBase}`}>Opp</th>
                        <th className={`${thBase}`}>Vegas</th>
                        <th className={`${thBase}`}>Time</th>
                        <th className={`${thBase}`}>Proj</th>
                        <th className={`${thBase}`}>Value</th>
                        <th className={`${thBase}`}>pOWN</th>
                      </tr>
                    </thead>
                    <tbody>
                      {getSortedBySalarySec("OF").length === 0 ? (
                        <tr>
                          <td
                            className="px-3 py-3 text-center text-slate-500"
                            colSpan={colsPlayer.length}
                          >
                            No rows.
                          </td>
                        </tr>
                      ) : (
                        getSortedBySalarySec("OF").map((r, i) => (
                          <PlayerRow key={`OF-${i}`} row={r} index={i} />
                        ))
                      )}
                    </tbody>
                  </table>
                </div>
              </Block>
              <Block title="Cash Core">
                <div className="w-full min-w-0">
                  <table className={tableClass} style={{ borderSpacing: 0 }}>
                    <thead className={`${headBg} sticky top-0 z-10`}>
                      <tr>
                        <th className={`${thBase} w-[26%]`}>Player</th>
                        <th className={`${thBase}`}>Salary</th>
                        <th className={`${thBase}`}>Team</th>
                        <th className={`${thBase}`}>Opp</th>
                        <th className={`${thBase}`}>Vegas</th>
                        <th className={`${thBase}`}>Time</th>
                        <th className={`${thBase}`}>Proj</th>
                        <th className={`${thBase}`}>Value</th>
                        <th className={`${thBase}`}>pOWN</th>
                      </tr>
                    </thead>
                    <tbody>
                      {getSec("Cash Core").length === 0 ? (
                        <tr>
                          <td
                            className="px-3 py-3 text-center text-slate-500"
                            colSpan={colsPlayer.length}
                          >
                            No rows.
                          </td>
                        </tr>
                      ) : (
                        getSec("Cash Core").map((r, i) => (
                          <PlayerRow key={`Core-${i}`} row={r} index={i} />
                        ))
                      )}
                    </tbody>
                  </table>
                </div>
              </Block>
              <Block title="Top Stacks">
                <div className="w-full min-w-0">
                  <table className={tableClass} style={{ borderSpacing: 0 }}>
                    <thead className={`${headBg} sticky top-0 z-10`}>
                      <tr>
                        <th className={`${thBase} w-[10%]`}>Team</th>
                        <th className={`${thBase} w-[12%]`}>Salary</th>
                        <th className={`${thBase} w-[9%]`}>Opp</th>
                        <th className={`${thBase} w-[21%]`}>Opp Pitcher</th>
                        <th className={`${thBase} w-[9%]`}>Vegas</th>
                        <th className={`${thBase} w-[12%]`}>Time</th>
                        <th className={`${thBase} w-[9%]`}>Proj</th>
                        <th className={`${thBase} w-[9%]`}>Value</th>
                        <th className={`${thBase} w-[9%]`}>pOWN</th>
                      </tr>
                    </thead>
                    <tbody>
                      {getSortedBySalarySec("Top Stacks").length === 0 ? (
                        <tr>
                          <td
                            className="px-3 py-3 text-center text-slate-500"
                            colSpan={colsStacks.length}
                          >
                            No rows.
                          </td>
                        </tr>
                      ) : (
                        getSortedBySalarySec("Top Stacks").map((r, i) => (
                          <StackRow key={`Stack-${i}`} row={r} index={i} />
                        ))
                      )}
                    </tbody>
                  </table>
                </div>
              </Block>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}